import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Download,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { formatFileSize } from "@/modules/hub/features/FileManagement/utils/formatters";
import { downloadAndSaveFilesystemNode } from "@/modules/hub/features/FileManagement/api/downloadFilesystemNode/downloadFilesystemNodeApi";
import { useToast } from "@/components/ui/use-toast";

export interface AttachmentItem {
  fileUuid: string;
  fileName: string;
  contentType?: string | null;
  fileSize?: number | null;
  publicUrl?: string | null;
}

interface FileAttachmentsDisplayProps {
  attachments: AttachmentItem[];
  onSelect: () => void;
  onRemove: (fileUuid: string) => void;
}

export default function FileAttachmentsDisplay({
  attachments,
  onSelect,
  onRemove,
}: FileAttachmentsDisplayProps) {
  const { toast } = useToast();
  const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);

  const handleDownload = async (attachment: AttachmentItem) => {
    try {
      setDownloadingUuid(attachment.fileUuid);
      await downloadAndSaveFilesystemNode(
        attachment.fileUuid,
        attachment.fileName,
      );
    } catch (error) {
      toast({
        title: "Download failed",
        description:
          error instanceof Error
            ? error.message
            : "Could not download the file.",
        variant: "destructive",
      });
    } finally {
      setDownloadingUuid(null);
    }
  };

  if (attachments.length === 0) {
    return (
      <div className="w-full">
        <button
          className="w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center bg-white dark:bg-slate-800 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all cursor-pointer group"
          onClick={onSelect}
          type="button"
        >
          <Upload className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors mb-3" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Click to add attachments
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            or browse from File Manager
          </p>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      <ul className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
        {attachments.map((attachment) => (
          <li
            className="flex items-center justify-between gap-3 px-4 py-3"
            key={attachment.fileUuid}
          >
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="text-blue-500 flex-shrink-0" size={18} />
              <div className="min-w-0">
                <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                  {attachment.fileName}
                </div>
                {typeof attachment.fileSize === "number" && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(attachment.fileSize)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                disabled={downloadingUuid === attachment.fileUuid}
                onClick={() => handleDownload(attachment)}
                size="sm"
                type="button"
                variant="ghost"
              >
                {downloadingUuid === attachment.fileUuid ? (
                  <Loader2 className="mr-1 animate-spin" size={14} />
                ) : (
                  <Download className="mr-1 text-blue-500" size={14} />
                )}
                Download
              </Button>
              <Button
                onClick={() => onRemove(attachment.fileUuid)}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Trash2 className="mr-1 text-red-500" size={14} />
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <Button onClick={onSelect} size="sm" type="button" variant="outline">
        <Plus className="mr-1" size={14} />
        Add more attachments
      </Button>
    </div>
  );
}
